<template>
  <v-card
    elevation="6"
    outlined
    class="login-wrapper center"
  >
    <v-card-text>

      <div class="logo-wrapper">
        <img src="~@/assets/img/logo.png" alt="Logo" class="logo">
      </div>

      <div class="form-wrapper">
        <b-alert v-model="hasError" variant="danger">
          {{ errorMessage }}
        </b-alert>

        <validation-observer ref="observer" v-slot="{ passes }" v-if="!open2FAModal">
          <b-form @submit.prevent="passes(onSubmit)">

            <validation-provider vid="email" rules="required|email" v-slot="{ valid, errors, changed }">
              <b-form-group label-for="input-email">
                <b-form-input
                  id="input-email"
                  v-model="form.email"
                  :state="onValidateField(valid, errors, changed)"
                  type="email"
                  :placeholder="$t('login.form.placeholderEmail')"
                ></b-form-input>
                <b-form-invalid-feedback class="input-live-feedback">{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider vid="password" name="password" rules="required|password" ref="password" v-slot="{ valid, errors, changed }">
              <b-form-group label-for="input-password">
                <b-form-input
                  id="input-password"
                  v-model="form.password"
                  :state="onValidateField(valid, errors, changed)"
                  type="password"
                  :placeholder="$t('login.form.placeholderPassword')"
                ></b-form-input>
                <b-form-invalid-feedback class="input-live-feedback">{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <v-btn type="submit" class="mt-4" color="primary" :disabled="isSubmitting ? true: false">
              <b-spinner small v-if="isSubmitting"></b-spinner>
              {{ $t('login.form.buttonLogin') }}
            </v-btn>

          </b-form>
        </validation-observer>

        <validation-observer ref="observer" v-slot="{ passes }" v-if="open2FAModal">
          <h5>Authenticate Your Account</h5>
          <b-form @submit.prevent="passes(onSubmit2FA)">
            <validation-provider vid="password" name="password" rules="min:6|max:6" ref="token" v-slot="{ valid, errors, changed }">
              <small>Enter 6-digit code from your two factor authenticator APP.</small>
              <b-form-group label-for="input-password">
                <b-form-input
                  id="input-2facode"
                  v-model="form.token"
                  :state="onValidateField(valid, errors, changed)"
                  type="text"
                  maxlength="6"
                  placeholder="000000"
                  class="mt-3"
                ></b-form-input>
                <b-form-invalid-feedback class="input-live-feedback">{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <v-btn type="submit" color="primary" class="mt-4" :disabled="isSubmitting ? true: false">
              <b-spinner small v-if="isSubmitting"></b-spinner>
              {{ $t('login.form.buttonLogin') }}
            </v-btn>

          </b-form>
        </validation-observer>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
  import { ValidationObserver, ValidationProvider } from 'vee-validate'
  import { validateField } from '@/helpers/formHelper.js'

  const getInitialData = () => ({
    form: {
      email: '',
      password: '',
      token: ''
    }
  })

  export default {
    components: {
      ValidationObserver,
      ValidationProvider
    },
    data: () => ({
      hasError: false,
      errorMessage: '',
      isSubmitting: false,
      ...getInitialData(),
      open2FAModal: false
    }),
    methods: {
      async onSubmit () {
        this.isSubmitting = true
        this.hasError = false

        try {
          const { errors, requiresTwoFactorAuthentication } = await this.$store.dispatch('user/LOGIN', this.form)

          if (errors && errors.length) {
            const { code } = errors[0]
            if (code === 'LOGIN_FAILED') {
              this.isSubmitting = false
              this.hasError = true
              this.errorMessage = this.$t('formError.invalidCredentials')
            }
            return
          } else if (requiresTwoFactorAuthentication) {
            this.isSubmitting = false
            this.open2FAModal = true
          }

          if (requiresTwoFactorAuthentication === null) {
            await this.$router.push({
              name: 'event-dashboard'
            })
          }
        } catch (err) {
          this.isSubmitting = false
          this.hasError = true
          this.errorMessage = this.$t('formError.unknownwError')
          console.error(err)
        }
      },
      async onSubmit2FA () {
        this.isSubmitting = true
        this.hasError = false

        try {
          const { errors } = await this.$store.dispatch('user/VALIDATE_2FA_TOKEN', this.form)

          if (errors && errors.length) {
            const { code } = errors[0]
            if (code === 'INVALID_OPERATION') {
              this.isSubmitting = false
              this.hasError = true
              this.form.token = ''
              this.errorMessage = this.$t('formError.invalidOperation')
            }
            return
          }

          await this.$router.push({
            name: 'event-dashboard'
          })
        } catch (err) {
          this.isSubmitting = false
          this.hasError = true
          this.errorMessage = this.$t('formError.unknownwError')
          console.error(err)
        }
      },
      onValidateField (valid, errors, changed) {
        return validateField(valid, errors, changed)
      },
      reset () {
        Object.assign(this.$data, getInitialData())
        requestAnimationFrame(() => {
          this.$refs.observer.reset()
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .login-wrapper{
    -webkit-border-radius: 10px 10px 10px 10px;
    border-radius: 10px 10px 10px 10px;
    background: #fff;
    position: relative;
    -webkit-box-shadow: 0 30px 60px 0 rgba(0,0,0,0.3);
    box-shadow: 0 30px 60px 0 rgba(0,0,0,0.3);
    padding: 3rem;

    .logo-wrapper{
      margin: 0 auto 3rem;
      max-width: 200px;
    }

    .logo{
      max-width: 100%;
      height: auto;
    }

    .form-wrapper{
      max-width: 300px;
      margin: 0 auto;
    }
  }

#input-2facode {
  padding-right: 15px;
  padding-left: 15px;
  letter-spacing: 25px;
  border: 0;
  width: 280px;
  min-width: 250px;
  max-width: 250px;
  margin: 0 13px 0 47px;

  &:focus {
    box-shadow: none;
  }
}

</style>
